import { CallDirection, CallStatus } from '../typings';
import { ReactComponent as CallIncomingIcon } from '../../../shared/assets/svg/call-incoming.svg';
import { ReactComponent as CallMissedIcon } from '../../../shared/assets/svg/call-missed.svg';
import { ReactComponent as CallNotConnectedIcon } from '../../../shared/assets/svg/call-not-connected.svg';
import { ReactComponent as CallOutgoingIcon } from '../../../shared/assets/svg/call-outgoing.svg';

type CallStyles = {
  bg: string;
  titleColor: string;
  iconColor: string;
  icon: JSX.Element;
  label: string;
  wabaLabelColor: string;
  wabaNumberColor: string;
};

const defaultStyles = (size: number, icon: JSX.Element): CallStyles => ({
  bg: '#ffffff',
  titleColor: '#2d2d2d',
  iconColor: '#3D906D',
  icon,
  label: 'Unknown status',
  wabaLabelColor: '#2d2d2d',
  wabaNumberColor: '#9B9B9B',
});

const getIncomingCallStyles = (status: CallStatus, size: number): CallStyles => {
  const icon = <CallIncomingIcon width={size} height={size} />;

  switch (status) {
    case CallStatus.INITIATED:
      return {
        bg: '#F0F5F0',
        titleColor: '#3D906D',
        iconColor: '#3D906D',
        icon,
        label: 'Incoming call',
        wabaLabelColor: '#2d2d2d',
        wabaNumberColor: '#9B9B9B',
      };
    case CallStatus.Connecting:
      return {
        bg: '#3D906D',
        titleColor: '#ffffffb3',
        iconColor: '#ffffff',
        icon,
        label: 'Incoming call',
        wabaLabelColor: '#fff',
        wabaNumberColor: '#ffffffb3',
      };
    case CallStatus.IN_PROGRESS:
      return {
        bg: '#3D906D',
        titleColor: '#ffffffb3',
        iconColor: '#ffffff',
        icon,
        label: 'Ongoing call',
        wabaLabelColor: '#fff',
        wabaNumberColor: '#ffffffb3',
      };
    case CallStatus.END:
      return {
        bg: '#ffffff',
        titleColor: '#2d2d2d',
        iconColor: '#3D906D',
        icon,
        label: 'Call ended',
        wabaLabelColor: '#2d2d2d',
        wabaNumberColor: '#9B9B9B',
      };
    case CallStatus.MISSED:
      return {
        bg: '#FDF7F7',
        titleColor: '#EF5350',
        iconColor: '#EF5350',
        icon: <CallMissedIcon width={size} height={size} />,
        label: 'Missed call',
        wabaLabelColor: '#2d2d2d',
        wabaNumberColor: '#9B9B9B',
      };
    case CallStatus.FAILED:
      return {
        bg: '#FDF7F7',
        titleColor: '#EF5350',
        iconColor: '#EF5350',
        icon: <CallNotConnectedIcon width={size} height={size} />,
        label: 'Failed call',
        wabaLabelColor: '#2d2d2d',
        wabaNumberColor: '#9B9B9B',
      };
    default:
      return defaultStyles(size, icon);
  }
};

const getOutgoingCallStyles = (status: CallStatus, size: number): CallStyles => {
  const icon = <CallOutgoingIcon width={size} height={size} />;

  switch (status) {
    case CallStatus.INITIATED:
      return {
        bg: '#F0F5F0',
        titleColor: '#3D906D',
        iconColor: '#3D906D',
        icon,
        label: 'Outgoing call',
        wabaLabelColor: '#2d2d2d',
        wabaNumberColor: '#9B9B9B',
      };
    case CallStatus.Connecting:
      return {
        bg: '#3D906D',
        titleColor: '#ffffffb3',
        iconColor: '#ffffff',
        icon,
        label: 'Outgoing call',
        wabaLabelColor: '#fff',
        wabaNumberColor: '#ffffffb3',
      };
    case CallStatus.Ringing:
      return {
        bg: '#3D906D',
        titleColor: '#ffffffb3',
        iconColor: '#ffffff',
        icon,
        label: 'Outgoing call',
        wabaLabelColor: '#fff',
        wabaNumberColor: '#ffffffb3',
      };
    case CallStatus.IN_PROGRESS:
      return {
        bg: '#3D906D',
        titleColor: '#ffffffb3',
        iconColor: '#ffffff',
        icon,
        label: 'Ongoing call',
        wabaLabelColor: '#fff',
        wabaNumberColor: '#ffffffb3',
      };
    case CallStatus.END:
      return {
        bg: '#ffffff',
        titleColor: '#2d2d2d',
        iconColor: '#3D906D',
        icon,
        label: 'Call ended',
        wabaLabelColor: '#2d2d2d',
        wabaNumberColor: '#9B9B9B',
      };
    case CallStatus.MISSED:
    case CallStatus.REJECT:
    case CallStatus.FAILED:
      return {
        bg: '#F8F8F8',
        titleColor: '#7A7A7A',
        iconColor: '#7A7A7A',
        icon: <CallNotConnectedIcon width={size} height={size} />,
        label: 'Not connected',
        wabaLabelColor: '#2d2d2d',
        wabaNumberColor: '#9B9B9B',
      };
    default:
      return defaultStyles(size, icon);
  }
};

export const getCallIConsAndStyles = (status: CallStatus, size = 16, direction: CallDirection): CallStyles => {
  return direction === CallDirection.INCOMING
    ? getIncomingCallStyles(status, size)
    : getOutgoingCallStyles(status, size);
};
